import React from "react";
import { Link, useRouteError } from "react-router-dom";

// The two screens the router shows when there is no page to show: an address
// that doesn't exist, and a page that crashed. Both sit inside the app, so the
// menu is still there, and both offer a way home. Same card as FeatureError.

function FallbackCard({ emoji, title, message, children, fullScreen = false }) {
  return (
    <div
      className={`flex items-center justify-center p-6 ${
        fullScreen ? "min-h-full bg-dupontCream dark:bg-darkPrimary" : "min-h-[300px]"
      }`}
    >
      <div className="max-w-[420px] text-center rounded-2xl border border-dupontCreamDk/60 dark:border-darkGold/10 bg-white dark:bg-darkCard shadow-md px-7 py-8">
        <div className="text-[44px] mb-2">{emoji}</div>
        <h1 className="font-display text-[20px] font-bold text-dupontBlue dark:text-darkText mb-2">
          {title}
        </h1>
        <p className="text-[15px] font-body text-dupontGray dark:text-darkTextSecondary leading-relaxed mb-5">
          {message}
        </p>
        <div className="flex flex-row flex-wrap items-center justify-center gap-[12px]">
          {children}
        </div>
      </div>
    </div>
  );
}

const PRIMARY_BUTTON =
  "inline-block px-6 py-2.5 bg-dupontBlue text-white rounded-full text-[15px] font-medium hover:bg-dupontBlue/90 transition-colors no-underline";
const SECONDARY_LINK =
  "text-[15px] font-medium text-dupontBlue dark:text-darkGold underline";

export function NotFound() {
  return (
    <FallbackCard
      emoji="🧭"
      title="We couldn't find that page"
      message="The link may be old or mistyped. Everything else is where you left it."
    >
      <Link to="/" className={PRIMARY_BUTTON}>
        Go to Home
      </Link>
    </FallbackCard>
  );
}

// `fullScreen`: shown in place of the whole app (the menu itself failed).
export function RouteError({ fullScreen = false }) {
  const error = useRouteError();
  // eslint-disable-next-line no-console
  console.error("A page failed to show:", error);
  return (
    <FallbackCard
      fullScreen={fullScreen}
      emoji="🌿"
      title="This page didn't load properly"
      message="Something hiccupped on our end. Your information is safe. Please refresh to try again."
    >
      <button type="button" onClick={() => window.location.reload()} className={PRIMARY_BUTTON}>
        Refresh the page
      </button>
      <Link to="/" className={SECONDARY_LINK}>
        Go to Home
      </Link>
    </FallbackCard>
  );
}
